import { ArrowUpRight } from "lucide-react";
import type { ReactNode } from "react";

import type { MygaProductSnapshot } from "@/lib/myga-application/schema";
import { cn } from "@/lib/utils";
import { GoldfinchLogo } from "@/packages/ui/goldfinch/logo";
import { NumberField } from "@/ui/entity/renderer/number-field";

export const MYGA_SHELL_LOOKS = {
  review: {
    gutter: "px-4",
    header: "py-3 sm:min-h-20 sm:px-8",
    logo: "h-8 w-28 sm:h-9 sm:w-36",
    main: "px-5 py-6 sm:px-8 sm:py-10",
  },
  customer: {
    gutter: "px-3 sm:px-8",
    header: "min-h-17",
    logo: "h-7 w-26 sm:h-8 sm:w-29",
    main: "px-3 py-4 sm:px-8 sm:py-10",
  },
};

export function MygaShellHeader(props: {
  review: boolean;
  product?: MygaProductSnapshot;
  children?: ReactNode;
}) {
  const { review, product, children } = props;
  const look = MYGA_SHELL_LOOKS[review ? "review" : "customer"];
  return (
    <header className="border-b border-gf-border bg-gf-surface">
      <div
        className={cn(
          "mx-auto flex min-h-16 max-w-7xl flex-wrap items-center justify-between gap-3",
          look.gutter,
          look.header,
        )}>
        <a
          href={review ? "/charm/myga-applications" : "/mygas"}
          aria-label="Goldfinch MYGA applications">
          <GoldfinchLogo className={cn("text-gf-ink-800", look.logo)} />
        </a>
        {product != null && <MygaHeaderProduct product={product} />}
        <nav
          aria-label="Application navigation"
          className="flex items-center gap-3 text-sm sm:gap-6">
          {review && (
            <a
              className="flex min-h-11 items-center gap-1.5 hover:underline"
              href="/charm">
              Back to Charm <ArrowUpRight className="size-3.5" aria-hidden />
            </a>
          )}
          {children}
        </nav>
      </div>
    </header>
  );
}

// Below lg the logo, product and links no longer fit one row, and each page
// shows the product in its own summary, so the header keeps to its links.
function MygaHeaderProduct(props: { product: MygaProductSnapshot }) {
  const { product } = props;
  return (
    <p
      className="ml-auto hidden text-gf-caption text-gf-text-muted lg:block"
      data-dd-privacy="hidden">
      <NumberField value={product.termYears} className="text-gf-caption" />
      -year MYGA at{" "}
      <NumberField
        value={product.rate}
        format="percent"
        decimals={2}
        className="text-gf-caption"
      />
      <NumberField
        value={product.premiumAmount}
        format="currency"
        decimals={0}
        className="ml-2 text-gf-caption font-semibold text-gf-text-strong"
      />
    </p>
  );
}
